import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { supabase } from './supabase';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Services } from './components/Services';
import { Comparison } from './components/Comparison';
import { Portfolio } from './components/Portfolio';
import { Advantages } from './components/Advantages';
import { Pricing } from './components/Pricing';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { AdminPanel } from './components/AdminPanel';
import { AdminLogin } from './components/AdminLogin';
import { SEOGuide } from './components/SEOGuide';
import './i18n/i18n';

const MainLayout = () => {
  const { i18n } = useTranslation();
  const isPolish = i18n.language === 'pl';

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <html lang={i18n.language} />
        <title>
          {isPolish
            ? 'Market Engine - Sklepy Internetowe B2B i Detaliczne | Tworzenie Sklepów Online Wielojęzycznych'
            : 'Market Engine - B2B & Retail E-commerce Stores | Multilingual Online Store Development'}
        </title>
        <meta
          name="description"
          content={
            isPolish
              ? 'Budujemy sklepy B2B dla hurtowni i sklepy detaliczne dla sprzedawców. Sklepy wielojęzyczne, optymalizacja katalogu produktów, profesjonalne zdjęcia, opisy i nazwy produktów.'
              : 'We build B2B e-commerce stores for wholesalers and retail online stores for sellers. Multilingual stores, product catalog optimization, professional photos, descriptions, and product names.'
          }
        />
        <meta
          name="keywords"
          content={
            isPolish
              ? 'sklep internetowy, b2b, hurtownia, sklep online, platforma b2b, sklep wielojęzyczny, katalog produktów, sprzedawcy allegro, tworzenie sklepów'
              : 'e-commerce, b2b store, online store, wholesale platform, multilingual store, product catalog, allegro sellers, online shop development'
          }
        />
        <link rel="canonical" href={`https://marketengine.pl${isPolish ? '/pl' : ''}`} />
      </Helmet>
      <Navbar />
      <main>
        <Hero />
        <Services />
        <Comparison />
        <Portfolio />
        <Advantages />
        <Pricing />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

const SEOLayout = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Complete SEO Guide | Market Engine - Professional E-commerce Development</title>
        <meta
          name="description"
          content="Comprehensive SEO guide for modern websites. Learn about search engine optimization, best practices, and how to improve your website's visibility."
        />
        <link rel="canonical" href="https://marketengine.pl/seo" />
      </Helmet>
      <Navbar />
      <main>
        <SEOGuide />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        console.error('Auth error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-2 border-blue-200/20"></div>
          <div className="w-12 h-12 rounded-full border-t-2 border-blue-500 animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <Routes>
          <Route path="/" element={<MainLayout />} />
          <Route path="/seo" element={<SEOLayout />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;